/** P1 route. Finishing marks the cohort welcomed (returning users land on the cockpit) and leaves for P2 or P4. */
import { useEffect, useState } from "react";
import { useApi, useRoute } from "../api/context";
import { useSignOut } from "../auth";
import { nextAfterWelcome } from "../checkin/handoff";
import { navigate, useLocation } from "../shell/history";
import { chairOwed } from "../shell/chair";
import { welcomedKey } from "../shell/landing";
import { useCohortData } from "../shell/data";
import { useAccountItems } from "../shell/runs";
import { GxTopBar } from "../shell/TopBar";
import { GxErrorBanner, GxWaiting } from "../theme/primitives";
import { Bank, BANK_STEPS } from "./Bank";

export function BankPage({ cohortId, teamId }: { readonly cohortId: string; readonly teamId: string | null }) {
  const data = useCohortData(cohortId, teamId, 0);
  const api = useApi();
  const { search } = useLocation();
  const [notice, setNotice] = useState<string | null>(null);
  // The pre-check (WP39) returns here at `?step=reveal`; the welcome hands off to it first.
  // The welcome shows once: a person who has seen it (this device) opens on the reveal, never the welcome again on every visit.
  const seen = (() => { try { return localStorage.getItem(welcomedKey(cohortId)) === "1"; } catch { return false; } })();
  const asked = BANK_STEPS.find((s) => s === new URLSearchParams(search).get("step"));
  const startStep = asked ?? "welcome";
  // Someone who has been through the welcome opens the current bank (Financials); the reveal is a replay behind `?step=reveal` (a link on that page), never the welcome again.
  const toBank = asked === undefined && seen;
  useEffect(() => { if (toBank) navigate(`/c/${cohortId}/financials`, { replace: true }); }, [toBank, cohortId]);
  if (toBank) return null;
  if (data.status === "no-team") return <WaitingForTeam />;
  if (data.error !== null) return <div className="gx-fullpage"><GxErrorBanner error={data.error} /></div>;
  if (data.status === "loading") return <div className="gx-stage gx-on-navy"><div className="gx-stage-body"><GxWaiting what="your bank" height={320} /></div></div>;
  const finish = () => {
    try { localStorage.setItem(welcomedKey(cohortId), "1"); } catch { /* the next visit then starts at P1 again */ }
    // "P2 (R0) or P4" — P2 while the round is not open yet (a draft round or none), otherwise the cockpit, because screens §P2 says P2 runs "while R1 is not open".
    // R17: the chair is where they go whenever the strategy and limits are not saved, open round or not; once saved, the cockpit.
    const notOpen = data.team.round === null || data.team.round.state === "draft";
    navigate(`/c/${cohortId}/${notOpen || chairOwed(data.team) ? "chair" : "cockpit"}`);
  };
  const onWelcomeDone = () => {
    try { localStorage.setItem(welcomedKey(cohortId), "1"); } catch { /* the welcome may show again */ }
    void nextAfterWelcome(api, cohortId, search).then((next) => { setNotice(next.notice); navigate(next.href); }); };
  return <Bank key={startStep} welcome={data.welcome} team={data.team} catalog={data.catalog} market={data.market} startStep={startStep} onWelcomeDone={onWelcomeDone} onFinish={finish} onStep={(step) => navigate(`/c/${cohortId}/bank?step=${step}`, { replace: true })} {...(notice === null ? {} : { notice })} />;
}

/** P1 before placement: the standard top bar (with sign out) over the wait. The shell's stream refreshes `authMe` on `membership.changed`, which re-renders this page with the team. */
function WaitingForTeam() {
  const me = useRoute("authMe", undefined, { staleTime: Infinity });
  const signOut = useSignOut();
  const accountItems = useAccountItems();
  return (
    <>
      <GxTopBar bankName="Governexus" subline="Bank Simulator" role={null} name={me.data?.principal.displayName ?? ""} clock={null} onSignOut={signOut} accountItems={accountItems} />
      <main className="gx-stage gx-on-navy" style={{ minHeight: "calc(100vh - var(--topbar-h))" }}>
        <div className="gx-stage-body" role="status">
          <h1 className="gx-stage-h">Your facilitator hasn&apos;t placed you on a team yet.</h1>
          <p className="gx-stage-note">This page moves on by itself as soon as you are placed.</p>
        </div>
      </main>
    </>
  );
}
